/**
 * Modal per creazione/modifica pianificatore
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Box,
  Chip,
  Typography,
  Autocomplete,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';

// Lista province italiane
const PROVINCE_ITALIANE = [
  'AG', 'AL', 'AN', 'AO', 'AP', 'AQ', 'AR', 'AT', 'AV', 'BA', 'BG', 'BI', 'BL', 'BN', 'BO',
  'BR', 'BS', 'BT', 'BZ', 'CA', 'CB', 'CE', 'CH', 'CL', 'CN', 'CO', 'CR', 'CS', 'CT', 'CZ',
  'EN', 'FC', 'FE', 'FG', 'FI', 'FM', 'FR', 'GE', 'GO', 'GR', 'IM', 'IS', 'KR', 'LC', 'LE',
  'LI', 'LO', 'LT', 'LU', 'MB', 'MC', 'ME', 'MI', 'MN', 'MO', 'MS', 'MT', 'NA', 'NO', 'NU',
  'OR', 'PA', 'PC', 'PD', 'PE', 'PG', 'PI', 'PN', 'PO', 'PR', 'PT', 'PU', 'PV', 'PZ', 'RA',
  'RC', 'RE', 'RG', 'RI', 'RM', 'RN', 'RO', 'SA', 'SI', 'SO', 'SP', 'SR', 'SS', 'SU', 'SV',
  'TA', 'TE', 'TN', 'TO', 'TP', 'TR', 'TS', 'TV', 'UD', 'VA', 'VB', 'VC', 'VE', 'VI', 'VR', 'VT', 'VV'
];

const REGIONI_ITALIANE = [
  'Abruzzo', 'Basilicata', 'Calabria', 'Campania', 'Emilia-Romagna',
  'Friuli-Venezia Giulia', 'Lazio', 'Liguria', 'Lombardia', 'Marche',
  'Molise', 'Piemonte', 'Puglia', 'Sardegna', 'Sicilia', 'Toscana',
  'Trentino-Alto Adige', 'Umbria', 'Valle d\'Aosta', 'Veneto'
];

const RUOLI_PIANIFICATORE = [
  'Pianificatore',
  'Responsabile Zona',
  'Coordinatore Rifornimenti',
  'Dispatcher',
  'Account Manager',
  'Altro'
];

const PlannerFormModal = ({ 
  open, 
  onClose, 
  planner, 
  suppliers = [],
  onSubmit, 
  loading 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isEdit = !!planner?.id;
  
  const [formData, setFormData] = useState({
    supplier_id: '',
    name: '',
    email: '',
    phone: '',
    mobile: '',
    role: 'Pianificatore',
    coverage_provinces: [],
    coverage_region: '',
    coverage_cities: [],
    working_hours: 'Lun-Ven 8:00-18:00',
    is_primary: false,
    emergency_contact: false,
    is_active: true,
    notes: ''
  });
  
  const [citiesInput, setCitiesInput] = useState('');

  useEffect(() => {
    if (planner) {
      let provinces = planner.coverage_provinces || [];
      let cities = planner.coverage_cities || [];
      
      // Parse JSON se necessario
      if (typeof provinces === 'string') {
        try { provinces = JSON.parse(provinces); } catch (e) { provinces = []; }
      }
      if (typeof cities === 'string') {
        try { cities = JSON.parse(cities); } catch (e) { cities = []; }
      }
      
      setFormData({
        supplier_id: planner.supplier_id || '',
        name: planner.name || '',
        email: planner.email || '',
        phone: planner.phone || '',
        mobile: planner.mobile || '',
        role: planner.role || 'Pianificatore',
        coverage_provinces: provinces,
        coverage_region: planner.coverage_region || '',
        coverage_cities: cities,
        working_hours: planner.working_hours || 'Lun-Ven 8:00-18:00',
        is_primary: planner.is_primary || false,
        emergency_contact: planner.emergency_contact || false,
        is_active: planner.is_active !== false,
        notes: planner.notes || ''
      });
      setCitiesInput(cities.join(', '));
    } else {
      // Reset form
      setFormData({
        supplier_id: '',
        name: '',
        email: '',
        phone: '',
        mobile: '',
        role: 'Pianificatore',
        coverage_provinces: [],
        coverage_region: '',
        coverage_cities: [],
        working_hours: 'Lun-Ven 8:00-18:00',
        is_primary: false,
        emergency_contact: false,
        is_active: true,
        notes: ''
      });
      setCitiesInput('');
    }
  }, [planner, open]);

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleProvincesChange = (event, newValue) => {
    setFormData(prev => ({ ...prev, coverage_provinces: newValue }));
  };

  const handleCitiesChange = (e) => {
    const value = e.target.value;
    setCitiesInput(value);
    // Converti stringa separata da virgola in array
    const cities = value.split(',').map(c => c.trim()).filter(c => c);
    setFormData(prev => ({ ...prev, coverage_cities: cities }));
  };

  const handleSubmit = () => {
    if (!formData.supplier_id || !formData.name) {
      return;
    }
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonIcon color="primary" />
        {isEdit ? 'Modifica Pianificatore' : 'Nuovo Pianificatore'}
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* Fornitore */}
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Fornitore</InputLabel>
              <Select
                value={formData.supplier_id}
                label="Fornitore"
                onChange={handleChange('supplier_id')}
              >
                {suppliers.map(s => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.company_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Dati anagrafici */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Nome e Cognome"
              value={formData.name}
              onChange={handleChange('name')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Ruolo</InputLabel>
              <Select
                value={formData.role}
                label="Ruolo"
                onChange={handleChange('role')}
              >
                {RUOLI_PIANIFICATORE.map(r => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Contatti */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Telefono"
              value={formData.phone}
              onChange={handleChange('phone')}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Cellulare"
              value={formData.mobile}
              onChange={handleChange('mobile')}
            />
          </Grid>

          {/* Copertura geografica */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Copertura Geografica
            </Typography>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Regione</InputLabel>
              <Select
                value={formData.coverage_region}
                label="Regione"
                onChange={handleChange('coverage_region')}
              >
                <MenuItem value="">Tutte</MenuItem>
                {REGIONI_ITALIANE.map(r => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              multiple
              options={PROVINCE_ITALIANE}
              value={formData.coverage_provinces}
              onChange={handleProvincesChange}
              renderInput={(params) => (
                <TextField {...params} label="Province coperte" placeholder="Seleziona..." />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="outlined" label={option} {...getTagProps({ index })} size="small" />
                ))
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Città coperte"
              value={citiesInput}
              onChange={handleCitiesChange}
              helperText="Inserisci le città separate da virgola (es: Milano, Roma, Napoli)"
            />
          </Grid>

          {/* Orari e flags */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Orari di lavoro"
              value={formData.working_hours}
              onChange={handleChange('working_hours')}
              placeholder="es: Lun-Ven 8:00-18:00"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', pt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_primary}
                    onChange={handleChange('is_primary')}
                  />
                }
                label="Contatto primario"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.emergency_contact}
                    onChange={handleChange('emergency_contact')}
                  />
                }
                label="Contatto emergenze"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleChange('is_active')}
                  />
                }
                label="Attivo"
              />
            </Box>
          </Grid>

          {/* Note */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Note"
              value={formData.notes}
              onChange={handleChange('notes')}
            />
          </Grid>
        </Grid>

        {!formData.supplier_id && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Seleziona un fornitore per creare il pianificatore
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Annulla
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !formData.supplier_id || !formData.name}
        >
          {loading ? 'Salvataggio...' : isEdit ? 'Salva Modifiche' : 'Crea Pianificatore'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlannerFormModal;
